/**
 * Cards Translations and Data - SINGLE SOURCE OF TRUTH for cards system
 * App-agnostic translations and icon configurations for badges, CTAs, etc.
 * Shared across all domains
 */

// Sprite path for card-specific icons
const ICONS_SPRITE_PATH = '/app_core/src/cards/icons/cards-icons.svg';

/**
 * Cards translations for badges and CTAs
 */
export const CARDS_TRANSLATIONS = {
    'es': {
        'cta': {
            'book': 'Ver disponibilidad',
            'stay': 'Encuentra tu alojamiento',
            'tickets': 'Ver opciones de entradas',
            'tour': 'Ver detalles del tour',
            'info': 'Ver detalles',
            'view': 'Ver más contenido',
            'explore': 'Explorar',
            'discover': 'Descubrir',
            'experience': 'Experimentar',
            'web': 'Visitar sitio web',
            'free': 'Reserva tu plaza gratis',
            'availability': 'Comprobar disponibilidad',
            'facebook': 'Abrir página de Facebook',
            'menu': 'Ver menú'
        },
        'badge': {
            'affiliate': 'Link Afiliado',
            'partner': 'Socio',
            'discount': 'Descuento',
            'popular': 'Popular',
            'best': 'Mejor',
            'recommended': 'Recomendado',
            'special': 'Especial',
            'limited': 'Limitado',
            'info': 'Info',
            'tip': 'Consejo útil',
            'fact': '¿Sabías esto?',
            'about': 'Lo esencial',
            'advise': 'Aviso',
            'photo': 'Foto-tip',
            'important': '¡Importante!'

        }
    },
    'en': {
        'cta': {
            'book': 'Check availability',
            'stay': 'Find your stay',
            'tickets': 'See ticket options',
            'tour': 'View tour details',
            'info': 'See details',
            'view': 'See more content',
            'explore': 'Explore',
            'discover': 'Discover',
            'experience': 'Experience',
            'web': 'Visit website',
            'free': 'Reserve your free spot',
            'availability': 'Check Availability',
            'facebook': 'Open Facebook page',
            'menu': 'View menu'
        },
        'badge': {
            'affiliate': 'Affiliate Link',
            'partner': 'Partner',
            'partner_local': 'Local Partner',
            'partner_discount': 'Partner Discount',
            'discount': 'Discount',
            'popular': 'Popular',
            'best': 'Best',
            'recommended': 'Recommended',
            'special': 'Special',
            'limited': 'Limited',
            'info': 'Quick Info',
            'tip': 'Useful Tip', // Pro Tip, Good to Know, Useful Tip
            'fact': 'Did you know?', // fun fact, did you know?
            'about': 'At a Glance', // At a Glance, Overview
            'advise': 'Heads up', // Heads up, Watch out, Caution, Alert
            'sponsored': 'Sponsored',
            'deal': 'Exclusive Deal',
            'photo': 'Photo Hack', // Insta Shot, Photogram, 
            'important': 'Important!'

        }
    }
};

/**
 * Badge configuration for cards
 * Each entry can specify:
 * - icon: SVG filename (without path)
 * - position: 'before' | 'after' (default: 'before')
 * - backgroundColor: CSS variable for background (e.g., 'var(--primary-color)', 'var(--secondary-color)', 'var(--tertiary-color)')
 * - textColor: Text color ('white' | 'black' | hex code)
 */
export const CARDS_BADGES = {
    'affiliate': {
        icon: 'link-solid-full.svg',
        position: 'before',
        backgroundColor: 'var(--text-muted-text-color)',
        textColor: 'white'
    },
    'info': {
        icon: 'circle-info-solid-full.svg',
        position: 'before',
        backgroundColor: 'var(--soft-blue)',
        textColor: 'white'
    },
    // Alias for favorites special card - uses same styling as 'info'
    'favorites': {
        icon: 'circle-info-solid-full.svg',
        position: 'before',
        backgroundColor: 'var(--soft-green)',
        textColor: 'white'
    },
    'discount': {
        icon: 'money-bill-1-regular-full.svg',
        position: 'before',
        backgroundColor: 'var(--soft-red)',
        textColor: 'white'
    },
    'partner': {
        icon: 'circle-info-solid-full.svg',
        position: 'before',
        backgroundColor: 'var(--header-muted-text-color)',
        textColor: 'white'
    },
    'tip': {
        icon: 'lightbulb-solid-full.svg',
        position: 'before',
        backgroundColor: 'var(--soft-yellow)',
        textColor: 'white'
    },
    'fact': {
        icon: 'face-surprise-solid-full.svg',
        position: 'before',
        backgroundColor: 'var(--soft-yellow)',
        textColor: 'white'
    },
    'about': {
        icon: 'eye-solid-full',
        position: 'before',
        backgroundColor: 'var(--soft-green)',
        textColor: 'white'
    },
    'advise': {
        icon: 'circle-exclamation-solid-full.svg',
        position: 'before',
        backgroundColor: 'var(--soft-red)',
        textColor: 'white'
    },
    'popular': {
        icon: 'star-solid-full.svg',
        position: 'before',
        backgroundColor: 'var(--soft-blue)',
        textColor: 'white'
    },
    'photo': {
        icon: 'camera-solid.svg',
        position: 'before',
        backgroundColor: 'var(--soft-yellow)',
        textColor: 'white'
    },
    'important': {
        icon: 'circle-exclamation-solid-full.svg',
        position: 'before',
        backgroundColor: 'var(--soft-red)',
        textColor: 'white'
    }
};

/**
 * Get translated text for badges or CTAs
 * @param {string} key - The translation key (e.g., 'affiliate', 'book')
 * @param {string} language - Language code ('en' or 'es')
 * @param {string} type - Type: 'badge' or 'cta'
 * @param {string} fallback - Fallback text if translation not found
 * @returns {string} Translated text
 */
export function getCardsTranslation(key, language = 'en', type = 'cta', fallback = '') {
    const lang = language === 'es' ? 'es' : 'en';
    return CARDS_TRANSLATIONS[lang]?.[type]?.[key] || fallback || key;
}

/**
 * Get badge text (convenience function)
 * @param {string} badgeKey - Badge key from database
 * @param {string} language - Language code ('en' or 'es')
 * @returns {string} Translated badge text
 */
export function getBadgeText(badgeKey, language = 'en') {
    return getCardsTranslation(badgeKey, language, 'badge', badgeKey);
}

/**
 * Get CTA text (convenience function)
 * @param {string} ctaKey - CTA key from database
 * @param {string} language - Language code ('en' or 'es')
 * @returns {string} Translated CTA text
 */
export function getCtaText(ctaKey, language = 'en') {
    return getCardsTranslation(ctaKey, language, 'cta', 'More info');
}

/**
 * Get badge configuration for a badge key
 * @param {string} key - The badge key (e.g., 'affiliate', 'info')
 * @returns {Object|null} Badge config with {icon, position, backgroundColor, textColor} or null if not found
 */
export function getBadgeIcon(key) {
    return CARDS_BADGES[key] || null;
}

/**
 * Get full icon path
 * @param {string} iconFilename - Icon filename (e.g., 'wallet-solid-full.svg')
 * @returns {string} Full path to icon
 */
export function getIconPath(iconFilename) {
    const cleanId = String(iconFilename || '').replace(/\.svg$/i, '');
    return `${ICONS_SPRITE_PATH}#${cleanId}`;
}

/**
 * Render icon HTML (SVG img tag)
 * @param {string} iconFilename - Icon filename
 * @param {string} alt - Alt text for accessibility
 * @param {string} className - CSS class name
 * @returns {string} HTML string for icon
 */
export function renderIcon(iconFilename, alt = '', className = 'cards-icon') {
    const iconHref = getIconPath(iconFilename);
    return `<svg class="${className}" aria-hidden="true" role="img"><use href="${iconHref}"></use></svg>`;
}

/**
 * Render badge text with optional icon (before or after)
 * @param {string} text - Badge text content
 * @param {string} badgeKey - Badge key for icon lookup
 * @param {string} className - CSS class for icon
 * @returns {string} HTML string with badge text and icon
 */
export function renderBadgeWithIcon(text, badgeKey, className = 'cards-badge-icon') {
    const iconConfig = getBadgeIcon(badgeKey);

    if (!iconConfig) {
        return text; // No icon, return text only
    }

    const iconHtml = renderIcon(iconConfig.icon, '', className);

    if (iconConfig.position === 'after') {
        return `${text} ${iconHtml}`;
    } else {
        return `${iconHtml} ${text}`;
    }
}

/**
 * Get badge colors (background and text) for a badge key
 * @param {string} badgeKey - Badge key from database
 * @returns {Object|null} Color config with {backgroundColor, textColor} or null if not found
 */
export function getBadgeColors(badgeKey) {
    const iconConfig = getBadgeIcon(badgeKey);
    if (!iconConfig || (!iconConfig.backgroundColor && !iconConfig.textColor)) {
        return null;
    }
    return {
        backgroundColor: iconConfig.backgroundColor || null,
        textColor: iconConfig.textColor || null
    };
}

